import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  checkIn,
  commitEscrow,
  inviteGuardian,
  listGuardians,
  removeGuardian,
  ROLE_COPY,
  type Guardian,
  type GuardianEscrowState,
} from '../lib/guardians/guardianApi';
import './guardians.css';

const MAX_GUARDIANS = 3;

function formatDate(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function GuardiansScreen() {
  const { api } = useAuth();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [guardians, setGuardians] = useState<Guardian[] | null>(null);
  const [threshold, setThreshold] = useState(2);
  const [shareHolders, setShareHolders] = useState(0);
  const [atFloor, setAtFloor] = useState(false);
  const [escrow, setEscrow] = useState<GuardianEscrowState | null>(null);
  const [checkinDue, setCheckinDue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const dash = await getMyVault(api);
      setVaultId(dash.vault.id);
      setCheckinDue(dash.vault.next_checkin_due_at);
      const res = await listGuardians(api, dash.vault.id);
      setGuardians(res.guardians);
      setThreshold(res.shamir_threshold);
      setShareHolders(res.active_share_holders);
      setAtFloor(res.at_threshold_floor);
      setEscrow(res.escrow);
    } catch {
      setError('We could not reach your guardians just now.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCheckIn(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await checkIn(api);
      setCheckinDue(res.next_checkin_due_at);
      setNotice('Thank you. We know you are here.');
    } catch {
      setError('That check-in did not go through.');
    } finally {
      setBusy(false);
    }
  }

  async function handleResend(g: Guardian): Promise<void> {
    if (vaultId === null) return;
    setBusy(true);
    setError(null);
    try {
      await inviteGuardian(api, vaultId, g.id);
      setNotice(`We sent ${g.invited_name} their invitation again.`);
      await load();
    } catch {
      setError('That invitation could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(g: Guardian): Promise<void> {
    if (vaultId === null) return;
    setBusy(true);
    setError(null);
    try {
      await removeGuardian(api, vaultId, g.id);
      setNotice(`${g.invited_name} is no longer a guardian.`);
      await load();
    } catch (e) {
      // The server refuses (409) any removal that would drop the vault below the
      // threshold — that would make it permanently unopenable. Say why, plainly.
      const status = (e as { status?: number }).status;
      setError(
        status === 409
          ? `Removing ${g.invited_name} would leave too few guardians to ever open your vault. Replace them instead — name someone new, and they take over the piece ${g.invited_name} holds.`
          : 'We could not remove them.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleSealEscrow(): Promise<void> {
    if (vaultId === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await commitEscrow(api, vaultId);
      setNotice(
        `Sealed. ${res.active_share_holders} of your guardians now hold a piece of your key — ` +
          'sealed to their own keys, which we do not have.',
      );
      await load();
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(
        status === 409
          ? 'At least two of your guardians need to have accepted and set up their key before this can be sealed. Until they have, there is nothing to seal a piece to.'
          : 'We could not seal your key to your guardians just now. Nothing was changed.',
      );
    } finally {
      setBusy(false);
    }
  }

  const live = (guardians ?? []).filter((g) => g.status === 'invited' || g.status === 'active');
  const active = live.filter((g) => g.status === 'active');
  const canAdd = live.length < MAX_GUARDIANS;
  /** Removing a share-holder here would make the vault unopenable forever. */
  const removalWouldBreakIt = (g: Guardian): boolean => g.holds_share && atFloor;

  return (
    <section className="guard">
      <p className="guard__kicker">Your trusted people</p>
      <h1 className="guard__title">Guardians.</h1>
      <p className="guard__sub">
        If you are not here — or cannot act — these are the people who can. The key to your vault is
        split between them. No one of them can open anything alone. Together, they can.
      </p>

      {error !== null && (
        <p role="alert" className="guard__alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="guard__notice">
          {notice}
        </p>
      )}

      {/* ---- check-in ---- */}
      <div className="guard__card guard__checkin">
        <p className="guard__label">The check-in</p>
        <h2 className="guard__cardTitle">
          {checkinDue === null ? 'You are here.' : `Next due ${formatDate(checkinDue)}`}
        </h2>
        <p className="guard__body">
          From time to time we ask you to confirm you are well. If you stop answering, we do not act
          straight away — there is a grace period, then your guardians are told, and only then does
          anything begin. <span className="guard__em">Silence is the signal.</span> Nobody has to
          notice anything for your messages to find their way.
        </p>
        <div className="la-cta-wrap">
          <button
            type="button"
            className="la-btn-primary"
            disabled={busy}
            onClick={() => void handleCheckIn()}
          >
            {busy ? 'One moment' : 'I am here'}
          </button>
        </div>
      </div>

      {/* ---- guardian list ---- */}
      {guardians !== null && guardians.length === 0 && (
        <div className="guard__card guard__empty">
          <h2 className="guard__cardTitle">Nobody yet holds a piece of your key.</h2>
          <p className="guard__body">
            Until you name someone, nothing can be released if you are not here to release it. Choose
            people who would still be standing when it matters — and who are not all in the same part
            of your life.
          </p>
          <div className="la-cta-wrap">
            <Link to="/guardians/new" className="la-btn-primary">
              Name your first guardian
            </Link>
          </div>
        </div>
      )}

      {guardians !== null && guardians.length > 0 && (
        <>
          <p className="guard__sectionLabel">
            {shareHolders} of {threshold} needed to open your vault
          </p>

          {shareHolders < threshold && active.length < live.length && (
            <p className="guard__body guard__waiting">
              You need {threshold} people holding a piece before your vault can ever be opened
              without you. {active.length === 0 ? 'Nobody has' : `Only ${active.length} has`} accepted
              so far.
            </p>
          )}

          <ul className="guard__list">
            {live.map((g) => (
              <li key={g.id} className="guard__card guard__item">
                <div className="guard__itemHead">
                  <div>
                    <h3 className="guard__name">{g.invited_name}</h3>
                    <p className="guard__meta">{g.invited_email}</p>
                  </div>
                  <span className={`guard__pill guard__pill--${g.status}`}>
                    {g.holds_share ? 'Holding their piece' : g.status === 'active' ? 'Accepted' : 'Invited'}
                  </span>
                </div>

                <p className="guard__role">
                  <strong>{ROLE_COPY[g.role].label}</strong> — {ROLE_COPY[g.role].blurb}
                </p>

                {g.status === 'invited' && (
                  <p className="guard__body guard__waiting">
                    Invited {formatDate(g.invitation_sent_at)}. They have not accepted yet, so they
                    do not hold a piece of your key. Until they do, they cannot help.
                  </p>
                )}

                {removalWouldBreakIt(g) && (
                  <p className="guard__body guard__waiting">
                    {g.invited_name} cannot be removed — with only {shareHolders} people holding a
                    piece, losing one would mean your vault could never be opened again. Name a
                    replacement instead, and they will take over the piece {g.invited_name} holds.
                  </p>
                )}

                <div className="guard__actions">
                  {g.status === 'invited' && (
                    <button
                      type="button"
                      className="la-btn-secondary"
                      disabled={busy}
                      onClick={() => void handleResend(g)}
                    >
                      Send again
                    </button>
                  )}
                  <Link to={`/guardians/${g.id}/replace`} className="la-btn-secondary">
                    Replace
                  </Link>
                  <button
                    type="button"
                    className="la-btn-secondary"
                    disabled={busy || removalWouldBreakIt(g)}
                    onClick={() => void handleRemove(g)}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {canAdd && (
            <div className="la-cta-wrap guard__addWrap">
              <Link to="/guardians/new" className="la-btn-primary">
                Name another guardian
              </Link>
            </div>
          )}

          {!canAdd && (
            <p className="guard__body guard__full">
              You have named all three. That is enough — more would not make you safer, only harder
              to reach.
            </p>
          )}

          {/* ---- the Perpetuity Covenant ----
              Not a release mechanism. This is the answer to "what if you go out of
              business in thirty years" — the one honest fear this product has to
              survive. We split the real vault key and seal a piece to each
              guardian's own key, which we do not hold and cannot open. If we
              vanish, two of them can still rebuild it without us. */}
          {escrow !== null && (
            <div className="guard__card guard__escrow">
              <p className="guard__label">If we are no longer here</p>
              <h2 className="guard__cardTitle">
                {escrow.committed_at !== null && !escrow.recommit_required
                  ? 'Your vault outlives us.'
                  : 'Your vault does not yet outlive us.'}
              </h2>

              <p className="guard__body">
                Your messages are kept under a key we hold, because we have to hold it to deliver
                them. That leaves one honest question: what happens to your vault if this company
                one day does not exist? So we split that key into three pieces and seal one to each
                of your guardians — to keys made on their own devices, which we never see.{' '}
                <span className="guard__em">
                  Any two of them can rebuild it without us. None of them can do it alone.
                </span>
              </p>

              {escrow.committed_at !== null && !escrow.recommit_required && (
                <p className="guard__body guard__sealed">
                  Sealed {formatDate(escrow.committed_at)}. {escrow.share_holders} of your guardians
                  hold a piece; {escrow.threshold} of them together can open your vault if we are
                  gone. There is nothing more for you to do.
                </p>
              )}

              {escrow.recommit_required && !escrow.can_commit && (
                <p className="guard__body guard__waiting">
                  Nothing is sealed yet. {escrow.threshold} of your guardians need to have accepted
                  and made their own key first — a piece can only be sealed to a key that exists.
                  {active.length > 0 && ` So far, ${active.length} of them has.`}
                </p>
              )}

              {escrow.recommit_required && escrow.can_commit && (
                <>
                  <p className="guard__body guard__waiting">
                    {escrow.epoch === 0
                      ? 'Your guardians have their keys. Nothing has been sealed to them yet.'
                      : 'Your guardians have changed since you last sealed this, so the pieces they hold no longer fit together. Seal it again.'}
                  </p>
                  <div className="la-cta-wrap">
                    <button
                      type="button"
                      className="la-btn-primary"
                      disabled={busy}
                      onClick={() => void handleSealEscrow()}
                    >
                      {busy ? 'Sealing' : 'Seal my key to my guardians'}
                    </button>
                  </div>
                  <p className="guard__body guard__fine">
                    Safe to do at any time. Each sealing makes a fresh set of pieces — which is
                    exactly what makes a removed guardian&rsquo;s old piece worthless.
                  </p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
